/**
 * EvidenceSnapshotDialog — unified auditor-ready view of what was collected
 * from GitHub or Google Workspace for a given control. Backed by
 * GET /api/controls/{controlId}/evidence-snapshot which returns a
 * point-in-time snapshot with:
 *   - source ('github' | 'google_workspace')
 *   - collection_timestamp
 *   - assessment { risk_rating, overall_assessment, ... }
 *   - counters
 *   - For GitHub: repositories[], organizations[]
 *   - For Google Workspace: non_compliant.users_without_2fa[],
 *     non_compliant.administrators[], non_compliant.suspended_users[]
 *
 * Also provides one-click download of the PDF and JSON snapshot artifacts.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Download, AlertTriangle, ShieldCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const StatCard = ({ label, value, tone = 'neutral', testid }) => {
  const bg = tone === 'bad' ? 'bg-amber-50 border-amber-200 text-amber-800'
    : tone === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`rounded-md border px-3 py-2 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value ?? '—'}</div>
    </div>
  );
};

const UserTable = ({ rows, extraCol, testid }) => {
  if (!rows || !rows.length) return <div className="text-xs text-slate-500 py-2">None.</div>;
  return (
    <div className="overflow-y-auto max-h-[280px] rounded border border-slate-200" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-700">Email</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-700">Name</th>
            {extraCol && <th className="text-left px-3 py-2 font-semibold text-slate-700">{extraCol.label}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.email + i} className="border-t border-slate-100" data-testid={`${testid}-row-${i}`}>
              <td className="px-3 py-2 font-mono text-slate-800">{r.email}</td>
              <td className="px-3 py-2 text-slate-700">{r.full_name || '—'}</td>
              {extraCol && <td className="px-3 py-2 text-slate-700">{r[extraCol.key] || '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EvidenceSnapshotDialog = ({ open, onClose, controlId, controlName }) => {
  const [loading, setLoading] = useState(false);
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !controlId) return;
    setLoading(true); setErr(''); setSnap(null);
    axios.get(`${API}/controls/${controlId}/evidence-snapshot`, authHeaders())
      .then((r) => setSnap(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'No snapshot available for this control'))
      .finally(() => setLoading(false));
  }, [open, controlId]);

  const source = snap?.source;
  const collectedAt = snap?.collection_timestamp;

  const downloadPdf = async () => {
    if (!snap) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/evidence/${snap.source}/${snap.evidence_id || 'latest'}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('pdf failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${snap.source}-evidence.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF download failed'); }
  };
  const downloadJson = () => {
    if (!snap) return;
    const blob = new Blob([JSON.stringify(snap, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `evidence-snapshot-${controlId.slice(0, 8)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON downloaded');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="evidence-snapshot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Auto-collected evidence
            {source && <Badge variant="outline" className="text-[10px] uppercase">{source.replace('_', ' ')}</Badge>}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            {controlName ? `${controlName} · ` : ''}
            Snapshot taken at <span className="font-medium">{collectedAt ? new Date(collectedAt).toLocaleString() : '—'}</span>
          </div>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading snapshot…
          </div>
        )}
        {err && !loading && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            <AlertTriangle className="w-4 h-4" /> {err}
          </div>
        )}

        {snap && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {source === 'github' && (
                <>
                  <StatCard label="Repos scanned" value={snap.counters?.repositories_scanned} testid="snap-stat-repos" />
                  <StatCard label="Orgs scanned" value={snap.counters?.organizations_scanned} testid="snap-stat-orgs" />
                  <StatCard label="Warnings" value={snap.counters?.warnings} tone={snap.counters?.warnings ? 'bad' : 'good'} testid="snap-stat-warnings" />
                  <StatCard label="Risk rating" value={snap.assessment?.risk_rating || '—'} testid="snap-stat-risk" />
                </>
              )}
              {source === 'google_workspace' && (
                <>
                  <StatCard label="Users total" value={snap.counters?.users_total} testid="snap-stat-users" />
                  <StatCard label="Users without 2FA" value={snap.counters?.users_2fa_off} tone={snap.counters?.users_2fa_off ? 'bad' : 'good'} testid="snap-stat-2fa" />
                  <StatCard label="Administrators" value={snap.counters?.admins_total} testid="snap-stat-admins" />
                  <StatCard label="Suspended" value={snap.counters?.suspended_total} testid="snap-stat-suspended" />
                </>
              )}
            </div>

            {source === 'google_workspace' && (
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-1.5">Users without 2FA / 2SV</div>
                  <UserTable rows={snap.non_compliant?.users_without_2fa || []} testid="snap-users-no-2fa" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-1.5">Administrators</div>
                  <UserTable rows={snap.non_compliant?.administrators || []} extraCol={{ key: 'type', label: 'Type' }} testid="snap-users-admins" />
                </div>
                {(snap.non_compliant?.suspended_users || []).length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-slate-800 mb-1.5">Suspended users</div>
                    <UserTable rows={snap.non_compliant.suspended_users} testid="snap-users-suspended" />
                  </div>
                )}
              </div>
            )}

            {source === 'github' && (snap.repositories || []).length > 0 && (
              <div>
                <div className="text-sm font-semibold text-slate-800 mb-1.5">Repositories ({snap.repositories.length})</div>
                <div className="overflow-y-auto max-h-[300px] rounded border border-slate-200" data-testid="snap-github-repos">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-slate-700">Repository</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-700">Private</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-700">Default branch</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-700">Branch protection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snap.repositories.slice(0, 100).map((r, i) => (
                        <tr key={r.name + i} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-slate-800">{r.name}</td>
                          <td className="px-3 py-2 text-slate-700">{r.private ? 'yes' : 'no'}</td>
                          <td className="px-3 py-2 text-slate-700">{r.default_branch || '—'}</td>
                          <td className="px-3 py-2 text-slate-700">{r.branch_protection || 'unknown'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="text-[11px] text-slate-500 flex items-center gap-1.5 pt-1">
              <FileText className="w-3 h-3" /> Point-in-time snapshot — the counts and lists above reflect the exact state collected. Re-collect to refresh.
            </div>
          </div>
        )}

        <DialogFooter className="mt-2 flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-400">Evidence ID: {snap?.evidence_id || '—'}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadJson} disabled={!snap} data-testid="snap-download-json">
              <Download className="w-3.5 h-3.5 mr-1" /> JSON
            </Button>
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={!snap} data-testid="snap-download-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={onClose} data-testid="snap-close">Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EvidenceSnapshotDialog;
